import {
  LazyQueryHookOptions,
  MutationHookOptions,
  OperationVariables,
  QueryHookOptions,
  useLazyQuery,
  useMutation,
  useQuery
} from '@apollo/client';
import {
  IActionAutoLabelEcommerceResponse,
  IAddGroupProductPayload,
  IAddGroupProductResponse,
  ICreateAutoLabelEcommercePayload,
  ICreateEcommerceComparisonParams,
  ICreateEcommerceComparisonResponse,
  IEcommerceObjectListResponse,
  IEcommerceProductListResponse,
  IEditAutoLabelEcommercePayload,
  IGetAutoLabelEcommerceByIdResponse,
  IGetAutoLabelEcommerceResponse,
  IGetEcommerceComparisonAdvanceMetricResponse,
  IGetEcommerceComparisonListResponse,
  IGetEcommerceComparisonPopularityResponse,
  IGetEcommerceComparisonPositioningResponse,
  IGetEcommerceComparisonSentimentAnalysisResponse,
  IGetEcommerceStreamResponse,
  IGetProductsPayload,
  IGroupProductListPayload,
  IGroupProductListResponse,
  IManageVisibilityEcommerceStreamParams,
  IManageVisibilityEcommerceStreamResponse,
  IResponseEcommerceGetAvgRating,
  IResponseEcommerceGetChronologicalPerformance,
  IResponseEcommerceGetChronologicalRating,
  IResponseEcommerceGetChronologicalReviews,
  IResponseEcommerceGetComparisonChronologicalRating,
  IResponseEcommerceGetSentimentAnalysis,
  IResponseEcommerceGetTopPeople,
  IResponseEcommerceGetTotalComment,
  IResponseEcommerceGetTotalNegative,
  IResponseEcommerceGetTotalPositive,
  IResponseEcommerceGetWordCloud,
  IUpdateGroupProductPayload,
  IGetEcommerceStreamCommentingToProductResponse,
  IGetEcommerceStreamCommentingToProductPayload,
  IEditEcommerceComparisonParams,
  IEditEcommerceComparisonResponse,
  IGetEcommerceComparisonById,
  IGetEcommerceTopProductResponse,
  IGetEcommerceTopStoreResponse,
  IResponseEcommerceGetTotalProducts,
  IResponseEcommerceGetTotalRating,
  IResponseEcommerceGetSentimentVsRating,
  IGetProductStagesPayload,
  IGetProductStagesResponse,
  ICreateProductStagePayload,
  ICreateProductStageResponse,
  IUpdateProductStagePayload,
  IUpdateProductStageResponse,
  IDeleteProductStagePayload,
  IDeleteProductStageResponse,
  IGetAssignProductStagePayload,
  IResponseEcommerceGetComparisonChronologicalReview
} from 'src/models/ecommerce';
import {
  ECOMMERCE_AUTO_LABEL_BY_ID,
  ECOMMERCE_AUTO_LABEL_LIST,
  ECOMMERCE_GET_AVG_RATING,
  ECOMMERCE_GET_CHRONOLOGICAL_PERFORMANCE,
  ECOMMERCE_GET_CHRONOLOGICAL_RATING,
  ECOMMERCE_GET_CHRONOLOGICAL_REVIEWS,
  ECOMMERCE_GET_COMPARISON_ADVANCE_METRIC,
  ECOMMERCE_GET_COMPARISON_CHRONOLOGICAL_RATING,
  ECOMMERCE_GET_COMPARISON_CHRONOLOGICAL_REVIEW,
  ECOMMERCE_GET_COMPARISON_ENTITY,
  ECOMMERCE_GET_COMPARISON_LIST,
  ECOMMERCE_GET_COMPARISON_POPULARITY,
  ECOMMERCE_GET_COMPARISON_POSITIONING,
  ECOMMERCE_GET_COMPARISON_SENTIMENT,
  ECOMMERCE_GET_IGNORE_STREAM,
  ECOMMERCE_GET_OBJECTS,
  ECOMMERCE_GET_PRODUCT_GROUPS,
  ECOMMERCE_GET_PRODUCT_STAGES,
  ECOMMERCE_GET_PRODUCTS,
  ECOMMERCE_GET_SENTIMENT_ANALYSIS,
  ECOMMERCE_GET_SENTIMENT_VS_RATING,
  ECOMMERCE_GET_STREAM,
  ECOMMERCE_GET_STREAM_COMMENTING_TO_PRODUCT,
  ECOMMERCE_GET_TOP_PEOPLE,
  ECOMMERCE_GET_TOP_PRODUCT,
  ECOMMERCE_GET_TOP_STORE,
  ECOMMERCE_GET_TOTAL_COMMENT,
  ECOMMERCE_GET_TOTAL_NEGATIVE,
  ECOMMERCE_GET_TOTAL_POSITIVE,
  ECOMMERCE_GET_TOTAL_PRODUCTS,
  ECOMMERCE_GET_TOTAL_RATING,
  ECOMMERCE_GET_WORD_CLOUD
} from './graphql/ecommerce/query';
import {
  ECOMMERCE_ADD_COMPARISON,
  ECOMMERCE_ADD_GROUP_PRODUCT,
  ECOMMERCE_CREATE_AUTO_LABEL,
  ECOMMERCE_CREATE_STAGE,
  ECOMMERCE_DELETE_AUTO_LABEL,
  ECOMMERCE_DELETE_GROUP_PRODUCT,
  ECOMMERCE_DELETE_STAGE,
  ECOMMERCE_EDIT_AUTO_LABEL,
  ECOMMERCE_EDIT_COMPARISON,
  ECOMMERCE_UPDATE_STAGE,
  ECOMMERCE_UPDATE_GROUP_PRODUCT,
  MANAGE_VISIBILITY_ON_ECOMMERE_STREAM,
  ECOMMERCE_ASSIGN_STAGE
} from './graphql/ecommerce/mutation';
import { IAdvanceSearchSocialMedia } from 'src/models/general';
import { useAppDispatch } from 'src/app/hooks';
import {
  reducerUpdateIgnoreStreamDataEcommerce,
  reducerUpdateStreamDataEcommerce
} from 'src/features/ecommerceStream';
import { OBJECT_MANAGEMENT_GET_GROUP_LIST } from './graphql/objectManagement/query';
import { IGetGroupObjectResponse } from './useObjectManagement';
import { IGroupObjectPayload } from 'src/models/objectManagement';
import { reducerUpdateEcommerceObjectList } from 'src/features/objectManagement';
import { IGetLabelSocialMediaResponse } from './useLabels';
import { GET_LABEL_FROM_PROJECT } from './graphql/labels/query';
import { reducerUpdateLabelListEcommerce } from 'src/features/label';

const useEcommerce = () => {
  const defaultOptions: LazyQueryHookOptions = {
    fetchPolicy: 'network-only',
    nextFetchPolicy: 'network-only'
  };

  const dispatch = useAppDispatch();

  const useGetTotalComment = (
    options?: LazyQueryHookOptions<
      IResponseEcommerceGetTotalComment,
      OperationVariables
    >
  ) => {
    return useLazyQuery<IResponseEcommerceGetTotalComment>(
      ECOMMERCE_GET_TOTAL_COMMENT,
      {
        ...defaultOptions,
        ...options
      }
    );
  };

  const useGetAvgRating = (
    options?: LazyQueryHookOptions<
      IResponseEcommerceGetAvgRating,
      OperationVariables
    >
  ) => {
    return useLazyQuery<IResponseEcommerceGetAvgRating>(
      ECOMMERCE_GET_AVG_RATING,
      {
        ...defaultOptions,
        ...options
      }
    );
  };

  const useGetTotalPositive = (
    options?: LazyQueryHookOptions<
      IResponseEcommerceGetTotalPositive,
      OperationVariables
    >
  ) => {
    return useLazyQuery<IResponseEcommerceGetTotalPositive>(
      ECOMMERCE_GET_TOTAL_POSITIVE,
      {
        ...defaultOptions,
        ...options
      }
    );
  };

  const useGetTotalNegative = (
    options?: LazyQueryHookOptions<
      IResponseEcommerceGetTotalNegative,
      OperationVariables
    >
  ) => {
    return useLazyQuery<IResponseEcommerceGetTotalNegative>(
      ECOMMERCE_GET_TOTAL_NEGATIVE,
      {
        ...defaultOptions,
        ...options
      }
    );
  };

  const useGetTotalProducts = (
    options?: LazyQueryHookOptions<
      IResponseEcommerceGetTotalProducts,
      OperationVariables
    >
  ) => {
    return useLazyQuery<IResponseEcommerceGetTotalProducts>(
      ECOMMERCE_GET_TOTAL_PRODUCTS,
      {
        ...defaultOptions,
        ...options
      }
    );
  };

  const useGetChronologicalPerformance = (
    options?: LazyQueryHookOptions<
      IResponseEcommerceGetChronologicalPerformance,
      OperationVariables
    >
  ) => {
    return useLazyQuery<IResponseEcommerceGetChronologicalPerformance>(
      ECOMMERCE_GET_CHRONOLOGICAL_PERFORMANCE,
      {
        ...defaultOptions,
        ...options
      }
    );
  };

  const useGetChronologicalRating = (
    options?: LazyQueryHookOptions<
      IResponseEcommerceGetChronologicalRating,
      OperationVariables
    >
  ) => {
    return useLazyQuery<IResponseEcommerceGetChronologicalRating>(
      ECOMMERCE_GET_CHRONOLOGICAL_RATING,
      {
        ...defaultOptions,
        ...options
      }
    );
  };

  const useGetChronologicalReviews = (
    options?: LazyQueryHookOptions<
      IResponseEcommerceGetChronologicalReviews,
      OperationVariables
    >
  ) => {
    return useLazyQuery<IResponseEcommerceGetChronologicalReviews>(
      ECOMMERCE_GET_CHRONOLOGICAL_REVIEWS,
      {
        ...defaultOptions,
        ...options
      }
    );
  };

  const useGetSentimentAnalysis = (
    options?: LazyQueryHookOptions<
      IResponseEcommerceGetSentimentAnalysis,
      OperationVariables
    >
  ) => {
    return useLazyQuery<IResponseEcommerceGetSentimentAnalysis>(
      ECOMMERCE_GET_SENTIMENT_ANALYSIS,
      {
        ...defaultOptions,
        ...options
      }
    );
  };

  const useGetWordCloud = (
    options?: LazyQueryHookOptions<
      IResponseEcommerceGetWordCloud,
      OperationVariables
    >
  ) => {
    return useLazyQuery<IResponseEcommerceGetWordCloud>(
      ECOMMERCE_GET_WORD_CLOUD,
      {
        ...defaultOptions,
        ...options
      }
    );
  };

  const useGetTopPeople = (
    options?: LazyQueryHookOptions<
      IResponseEcommerceGetTopPeople,
      OperationVariables
    >
  ) => {
    return useLazyQuery<IResponseEcommerceGetTopPeople>(
      ECOMMERCE_GET_TOP_PEOPLE,
      {
        ...defaultOptions,
        ...options
      }
    );
  };

  const useGetEcommerceComparisonList = (
    options?: LazyQueryHookOptions<
      IGetEcommerceComparisonListResponse,
      { projectId: string }
    >
  ) => {
    return useLazyQuery<
      IGetEcommerceComparisonListResponse,
      { projectId: string }
    >(ECOMMERCE_GET_COMPARISON_LIST, options);
  };

  const useCreateEcommerceComparison = (
    options?: MutationHookOptions<
      ICreateEcommerceComparisonResponse,
      ICreateEcommerceComparisonParams
    >
  ) =>
    useMutation<
      ICreateEcommerceComparisonResponse,
      ICreateEcommerceComparisonParams
    >(ECOMMERCE_ADD_COMPARISON, options);

  const useGetComparisonPositioning = (
    options?: LazyQueryHookOptions<
      IGetEcommerceComparisonPositioningResponse,
      IAdvanceSearchSocialMedia
    >
  ) =>
    useLazyQuery(ECOMMERCE_GET_COMPARISON_POSITIONING, {
      ...defaultOptions,
      ...options
    });

  const useGetComparisonChronologicalRating = (
    options?: LazyQueryHookOptions<
      IResponseEcommerceGetComparisonChronologicalRating,
      IAdvanceSearchSocialMedia
    >
  ) =>
    useLazyQuery(ECOMMERCE_GET_COMPARISON_CHRONOLOGICAL_RATING, {
      ...defaultOptions,
      ...options
    });

  const useGetComparisonChronologicalReview = (
    options?: LazyQueryHookOptions<
      IResponseEcommerceGetComparisonChronologicalReview,
      IAdvanceSearchSocialMedia
    >
  ) =>
    useLazyQuery(ECOMMERCE_GET_COMPARISON_CHRONOLOGICAL_REVIEW, {
      ...defaultOptions,
      ...options
    });

  const useGetComparisonPopularity = (
    options?: LazyQueryHookOptions<
      IGetEcommerceComparisonPopularityResponse,
      IAdvanceSearchSocialMedia
    >
  ) =>
    useLazyQuery(ECOMMERCE_GET_COMPARISON_POPULARITY, {
      ...defaultOptions,
      ...options
    });

  const useGetComparisonSentimentAnalysis = (
    options?: LazyQueryHookOptions<
      IGetEcommerceComparisonSentimentAnalysisResponse,
      IAdvanceSearchSocialMedia
    >
  ) =>
    useLazyQuery(ECOMMERCE_GET_COMPARISON_SENTIMENT, {
      ...defaultOptions,
      ...options
    });

  const useGetComparisonAdvanceMetric = (
    options?: LazyQueryHookOptions<
      IGetEcommerceComparisonAdvanceMetricResponse,
      IAdvanceSearchSocialMedia
    >
  ) =>
    useLazyQuery(ECOMMERCE_GET_COMPARISON_ADVANCE_METRIC, {
      ...defaultOptions,
      ...options
    });

  const useGetStream = (
    options?: LazyQueryHookOptions<
      IGetEcommerceStreamResponse,
      IAdvanceSearchSocialMedia
    >
  ) =>
    useLazyQuery(ECOMMERCE_GET_STREAM, {
      ...defaultOptions,
      ...options,
      onCompleted: (res) => {
        dispatch(
          reducerUpdateStreamDataEcommerce(res?.ecommerce_getStreamEcommerce)
        );
      }
    });

  const useGetIgnoreStream = (
    options?: LazyQueryHookOptions<
      IGetEcommerceStreamResponse,
      IAdvanceSearchSocialMedia
    >
  ) =>
    useLazyQuery(ECOMMERCE_GET_IGNORE_STREAM, {
      ...defaultOptions,
      ...options,
      onCompleted: (res) => {
        dispatch(
          reducerUpdateIgnoreStreamDataEcommerce(
            res?.ecommerce_getIgnoredStreamEcommerce
          )
        );
      }
    });

  const [ignorePost, responseIgnorePost] = useMutation<
    IManageVisibilityEcommerceStreamResponse,
    IManageVisibilityEcommerceStreamParams
  >(MANAGE_VISIBILITY_ON_ECOMMERE_STREAM);

  const [restorePost, responseRestorePost] = useMutation<
    IManageVisibilityEcommerceStreamResponse,
    IManageVisibilityEcommerceStreamParams
  >(MANAGE_VISIBILITY_ON_ECOMMERE_STREAM);

  const useGetEcommerceObjectList = (
    options?: LazyQueryHookOptions<
      IEcommerceObjectListResponse,
      OperationVariables
    >
  ) => {
    return useLazyQuery<IEcommerceObjectListResponse>(ECOMMERCE_GET_OBJECTS, {
      ...defaultOptions,
      ...options,
      onCompleted: (res) => {
        dispatch(reducerUpdateEcommerceObjectList(res?.ecommerce_getObject));
      }
    });
  };

  const useGetEcommerceGroups = (
    options?: QueryHookOptions<IGetGroupObjectResponse, IGroupObjectPayload>
  ) =>
    useQuery<IGetGroupObjectResponse, IGroupObjectPayload>(
      OBJECT_MANAGEMENT_GET_GROUP_LIST,
      options
    );

  const useGetEcommerceProductGroups = (
    options?: QueryHookOptions<
      IGroupProductListResponse,
      IGroupProductListPayload
    >
  ) =>
    useQuery<IGroupProductListResponse, IGroupProductListPayload>(
      ECOMMERCE_GET_PRODUCT_GROUPS,
      options
    );

  const [addProductGroup, responseAddProductGroup] = useMutation<
    IAddGroupProductResponse,
    IAddGroupProductPayload
  >(ECOMMERCE_ADD_GROUP_PRODUCT);

  const [updateProductGroup, responseUpdateProductGroup] = useMutation<
    IAddGroupProductResponse,
    IUpdateGroupProductPayload
  >(ECOMMERCE_UPDATE_GROUP_PRODUCT);

  const useGetEcommerceProducts = (
    options?: QueryHookOptions<
      IEcommerceProductListResponse,
      IGetProductsPayload
    >
  ) =>
    useQuery<IEcommerceProductListResponse, IGetProductsPayload>(
      ECOMMERCE_GET_PRODUCTS,
      options
    );

  const useGetEcommerceStreamCommentingToProduct = (
    options?: QueryHookOptions<
      IGetEcommerceStreamCommentingToProductResponse,
      IGetEcommerceStreamCommentingToProductPayload
    >
  ) =>
    useQuery<
      IGetEcommerceStreamCommentingToProductResponse,
      IGetEcommerceStreamCommentingToProductPayload
    >(ECOMMERCE_GET_STREAM_COMMENTING_TO_PRODUCT, options);

  const [deleteProductGroup, responseDeleteProductGroup] = useMutation<
    IAddGroupProductResponse,
    { id: string }
  >(ECOMMERCE_DELETE_GROUP_PRODUCT);

  //Label & Auto Label E-Commerce
  const useGetEcommerceLabel = (
    options?: LazyQueryHookOptions<
      IGetLabelSocialMediaResponse,
      OperationVariables
    >
  ) => {
    return useLazyQuery<IGetLabelSocialMediaResponse>(GET_LABEL_FROM_PROJECT, {
      ...defaultOptions,
      ...options,
      onCompleted: (res) => {
        dispatch(
          reducerUpdateLabelListEcommerce(
            res?.labellingSystem_getLabelFromProjects
          )
        );
      }
    });
  };

  const useGetEcommerceAutoLabel = (
    options?: QueryHookOptions<
      IGetAutoLabelEcommerceResponse,
      { projectId: string }
    >
  ) =>
    useQuery<IGetAutoLabelEcommerceResponse, { projectId: string }>(
      ECOMMERCE_AUTO_LABEL_LIST,
      options
    );

  const useGetEcommerceAutoLabelById = (
    options?: LazyQueryHookOptions<
      IGetAutoLabelEcommerceByIdResponse,
      OperationVariables
    >
  ) => {
    return useLazyQuery<IGetAutoLabelEcommerceByIdResponse>(
      ECOMMERCE_AUTO_LABEL_BY_ID,
      {
        ...defaultOptions,
        ...options
      }
    );
  };

  const [createAutoLabelEcommerce, responseCreateAutoLabelEcommerce] =
    useMutation<
      IActionAutoLabelEcommerceResponse,
      ICreateAutoLabelEcommercePayload
    >(ECOMMERCE_CREATE_AUTO_LABEL);

  const [editAutoLabelEcommerce, responseEditAutoLabelEcommerce] = useMutation<
    IActionAutoLabelEcommerceResponse,
    IEditAutoLabelEcommercePayload
  >(ECOMMERCE_EDIT_AUTO_LABEL);

  const [deleteAutoLabelEcommerce, responseDeleteAutoLabelEcommerce] =
    useMutation<IActionAutoLabelEcommerceResponse, { autoLabelId: string }>(
      ECOMMERCE_DELETE_AUTO_LABEL
    );

  const useEditEcommerceComparison = (
    options?: MutationHookOptions<
      IEditEcommerceComparisonResponse,
      IEditEcommerceComparisonParams
    >
  ) =>
    useMutation<
      IEditEcommerceComparisonResponse,
      IEditEcommerceComparisonParams
    >(ECOMMERCE_EDIT_COMPARISON, options);

  const useGetEcommerceComparisonById = (
    options?: QueryHookOptions<
      IGetEcommerceComparisonById,
      { comparisonId: string }
    >
  ) =>
    useQuery<IGetEcommerceComparisonById, { comparisonId: string }>(
      ECOMMERCE_GET_COMPARISON_ENTITY,
      options
    );

  const useGetTopProduct = (
    options?: LazyQueryHookOptions<
      IGetEcommerceTopProductResponse,
      OperationVariables
    >
  ) => {
    return useLazyQuery<IGetEcommerceTopProductResponse>(
      ECOMMERCE_GET_TOP_PRODUCT,
      {
        ...defaultOptions,
        ...options
      }
    );
  };

  const useGetTopStore = (
    options?: LazyQueryHookOptions<
      IGetEcommerceTopStoreResponse,
      OperationVariables
    >
  ) => {
    return useLazyQuery<IGetEcommerceTopStoreResponse>(
      ECOMMERCE_GET_TOP_STORE,
      {
        ...defaultOptions,
        ...options
      }
    );
  };

  const useGetTotalRating = (
    options?: LazyQueryHookOptions<
      IResponseEcommerceGetTotalRating,
      OperationVariables
    >
  ) => {
    return useLazyQuery<IResponseEcommerceGetTotalRating>(
      ECOMMERCE_GET_TOTAL_RATING,
      {
        ...defaultOptions,
        ...options
      }
    );
  };

  const useGetSentimentVsRating = (
    options?: LazyQueryHookOptions<
      IResponseEcommerceGetSentimentVsRating,
      OperationVariables
    >
  ) => {
    return useLazyQuery<IResponseEcommerceGetSentimentVsRating>(
      ECOMMERCE_GET_SENTIMENT_VS_RATING,
      {
        ...defaultOptions,
        ...options
      }
    );
  };

  const useGetProductStages = (
    options?: QueryHookOptions<
      IGetProductStagesResponse,
      IGetProductStagesPayload
    >
  ) =>
    useQuery<IGetProductStagesResponse, IGetProductStagesPayload>(
      ECOMMERCE_GET_PRODUCT_STAGES,
      options
    );

  const [updateProductStage, responseUpdateProductStage] = useMutation<
    IUpdateProductStageResponse,
    IUpdateProductStagePayload
  >(ECOMMERCE_UPDATE_STAGE);

  const [createProductStage, responseCreateProductStage] = useMutation<
    ICreateProductStageResponse,
    ICreateProductStagePayload
  >(ECOMMERCE_CREATE_STAGE);

  const [deleteProductStage, responseDeleteProductStage] = useMutation<
    IDeleteProductStageResponse,
    IDeleteProductStagePayload
  >(ECOMMERCE_DELETE_STAGE);

  const [assignProductStage, responseAssignProductStage] = useMutation<
    any,
    IGetAssignProductStagePayload
  >(ECOMMERCE_ASSIGN_STAGE);

  return {
    useGetTotalComment,
    useGetAvgRating,
    useGetTotalPositive,
    useGetTotalNegative,
    useGetTotalProducts,
    useGetChronologicalPerformance,
    useGetChronologicalRating,
    useGetChronologicalReviews,
    useGetSentimentAnalysis,
    useGetWordCloud,
    useGetTopPeople,
    useGetEcommerceComparisonList,
    useCreateEcommerceComparison,
    useGetComparisonPositioning,
    useGetComparisonChronologicalRating,
    useGetComparisonChronologicalReview,
    useGetComparisonPopularity,
    useGetComparisonSentimentAnalysis,
    useGetComparisonAdvanceMetric,
    useGetStream,
    useGetIgnoreStream,
    ignorePost,
    responseIgnorePost,
    restorePost,
    responseRestorePost,
    useGetEcommerceObjectList,
    useGetEcommerceGroups,
    useGetEcommerceProductGroups,
    addProductGroup,
    responseAddProductGroup,
    updateProductGroup,
    responseUpdateProductGroup,
    useGetEcommerceProducts,
    deleteProductGroup,
    responseDeleteProductGroup,
    useGetEcommerceLabel,
    useGetEcommerceStreamCommentingToProduct,
    useGetEcommerceAutoLabel,
    createAutoLabelEcommerce,
    responseCreateAutoLabelEcommerce,
    editAutoLabelEcommerce,
    responseEditAutoLabelEcommerce,
    deleteAutoLabelEcommerce,
    responseDeleteAutoLabelEcommerce,
    useGetEcommerceAutoLabelById,
    useEditEcommerceComparison,
    useGetEcommerceComparisonById,
    useGetTopProduct,
    useGetTopStore,
    useGetTotalRating,
    useGetSentimentVsRating,
    useGetProductStages,
    createProductStage,
    responseCreateProductStage,
    updateProductStage,
    responseUpdateProductStage,
    deleteProductStage,
    responseDeleteProductStage,
    assignProductStage,
    responseAssignProductStage
  };
};

export default useEcommerce;
